<template>
  <WizardModal
    ref="wizardRef"
    v-model="currentStep"
    :show-modal="isVisible"
    :modal-title="modalTitle"
    :steps="wizardSteps"
    :size="(size as any)"
    :loading="isLoading"
    :allow-step-click="isEditMode"
    :linear="!isEditMode"
    :custom-buttons="isEditMode"
    :next-label="isEditMode ? nextButtonLabel : undefined"
    :disable-next="isEditMode ? disableNextButton : undefined"
    @finish="handleFinish"
    @cancel="handleCancel"
    @close="handleClose"
  >
    <!-- Step 0: Basic Information -->
    <template #step-0>
      <OnboardingStep0Registration
        v-model="formData"
        :errors="formErrors"
        :edit-mode="isEditMode"
        @validate="handleStep0Validate"
      />
    </template>

    <!-- Step 1: Permissions (Edit mode only) -->
    <template v-if="isEditMode" #step-1>
      <OnboardingStep1Permissions
        v-model="selectedGroups"
        :available-groups="availableGroups"
        :loading="loadingGroups"
        :valores-de-atributo="{ mcp_scopes: formData.mcp_scopes, dw_scopes: formData.dw_scopes }"
        @update:valores-de-atributo="aplicaValoresDeAtributo"
      />
    </template>

    <!-- Step 2: Franchises (Edit mode only, conditional) -->
    <template v-if="isEditMode" #step-2>
      <OnboardingStep2Franchises
        v-if="!pulaFranquias"
        v-model="selectedFranchises"
        :available-franchises="availableFranchises"
        :loading="loadingFranchises"
      />
    </template>

    <!-- Step 3 (Edit) / Step 1 (Create): Profile Picture or Review -->
    <template v-if="isEditMode" #step-3>
      <OnboardingStep3ProfilePic
        v-model="profileImage"
        :username="formData.username"
      />
    </template>

    <!-- Step 1 (Create): Review -->
    <template v-if="!isEditMode" #step-1>
      <div class="wizard-step-content review-step">
        <div class="alert alert-success">
          <i class="pi pi-check-circle"></i> {{ t('Revise as informações antes de criar o usuário.') }}
        </div>

        <div class="review-section">
          <h4>{{ t('Informações Básicas') }}</h4>
          <div class="review-item">
            <span class="review-label">{{ t('Nome de usuário') }}:</span>
            <span class="review-value">{{ formData.username }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">{{ t('Nome') }}:</span>
            <span class="review-value">{{ formData.name }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">{{ t('Documento') }}:</span>
            <span class="review-value">{{ formData.documento }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">{{ t('Email') }}:</span>
            <span class="review-value">{{ formData.email }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">{{ t('Telefone') }}:</span>
            <span class="review-value">{{ formData.phone_number }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">{{ t('Idioma') }}:</span>
            <span class="review-value">{{ getLanguageName(formData.language) }}</span>
          </div>
          <div v-if="formData.mcp_scopes" class="review-item">
            <span class="review-label">{{ t('Permissões MCP') }}:</span>
            <span class="review-value">{{ formData.mcp_scopes }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">{{ t('Cor') }}:</span>
            <span class="review-value">
              <span class="color-preview" :style="{ backgroundColor: formData.bgcolor }"></span>
              {{ formData.bgcolor }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import OnboardingStep0Registration from './onboarding/OnboardingStep0Registration.vue';
import OnboardingStep1Permissions from './onboarding/OnboardingStep1Permissions.vue';
import OnboardingStep2Franchises from './onboarding/OnboardingStep2Franchises.vue';
import OnboardingStep3ProfilePic from './onboarding/OnboardingStep3ProfilePic.vue';
import { useUsers } from '@/composables/useUsers';
import { useRegrasDeGrupos } from '@/composables/useRegrasDeGrupos';
import { useLanguages } from '@/composables/useLanguages';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type WizardMode = 'create' | 'edit';

interface User {
  username?: string;
  sub?: string;
  name?: string;
  email?: string;
  phone_number?: string;
  documento?: string;
  language?: string;
  bgcolor?: string;
  versaoSistema?: string | number;
  mcp_scopes?: string;
  dw_scopes?: string;
  Usuario?: User;
  [key: string]: unknown;
}

interface FormData {
  username: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone_number: string;
  documento: string;
  language: string;
  versaoSistema: string;
  bgcolor: string;
  mcp_scopes: string;
  dw_scopes: string;
}

interface FormErrors {
  [field: string]: string | undefined;
}

interface AvailableFranchise {
  id: string;
  name: string;
  location: string;
  status: string;
}

interface FranchiseEntry {
  Dados?: { Nome?: string; Cidade?: string; [key: string]: unknown };
  Nome?: string;
  Cidade?: string;
  [key: string]: unknown;
}

interface UserPermissions {
  Groups?: string[];
  groups?: string[];
  Franquias?: Record<string, unknown>;
  franquias?: Record<string, unknown>;
}

interface LanguageOption {
  ID: string;
  Name: string;
}

interface WizardStep {
  title: string;
  description: string;
  icon?: string;
  validate: () => boolean;
}

interface ModalOpenPayload {
  mode?: WizardMode;
  user?: User;
}

const { t } = useI18n();
const {
  fetchUserBySub,
  createUser,
  updateUser,
  fetchUserGroups,
  fetchUserPermissions,
  toggleUserGroup,
  toggleUserFranquia,
  uploadProfileImage
} = useUsers();

// Props
const props = withDefaults(defineProps<{
  show?: boolean;
  mode?: WizardMode;
  user?: User | null;
}>(), {
  show: false,
  mode: 'create',
  user: null
});

// Note: Centralized pattern uses eventBus instead of local emits
// Events emitted via eventBus: user-wizard.closed, user-wizard.saved, users.refresh

// Internal state for centralized ModalManager
const internalShow = ref<boolean>(false);
const internalMode = ref<WizardMode>('create');
const internalUser = ref<User | null>(null);

// Computed - use internal state or fallback to props
const isVisible = computed<boolean>(() => internalShow.value || Boolean(props.show));
const currentMode = computed<WizardMode>(() => internalMode.value || props.mode);
const currentUser = computed<User | null>(() => internalUser.value || props.user || null);

// Mode computed
const isEditMode = computed<boolean>(() => currentMode.value === 'edit');

// Event handlers for centralized ModalManager
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('UserWizard', 'handleModalOpen', 'Modal open event received', data);
  if (data?.mode) {
    internalMode.value = data.mode;
  }
  if (data?.user) {
    internalUser.value = data.user;
  }
  internalShow.value = true;

  // Load data if in edit mode
  if (isEditMode.value && internalUser.value) {
    await loadUserData();
    await loadGroups();
    await loadUserPermissions();
  }
};

// Modal configuration
const modalTitle = computed<string>(() => isEditMode.value ? t('Editar Usuário') : t('Criar Usuário'));
const size = computed<string>(() => 'large');

// Wizard state
const wizardRef = ref<unknown>(null);
const currentStep = ref<number>(0);
const isLoading = ref<boolean>(false);

// Form data
const formData = ref<FormData>({
  username: '',
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone_number: '',
  documento: '',
  language: 'pt-BR',
  versaoSistema: '0',
  bgcolor: '#3c8dbc',
  mcp_scopes: '',
  dw_scopes: ''
});


const formErrors = ref<FormErrors>({});
const isStep0Valid = ref<boolean>(false);

// Permissions data (edit mode only)
const selectedGroups = ref<string[]>([]);
const availableGroups = ref<string[]>([]);
const loadingGroups = ref<boolean>(false);
const initialGroups = ref<string[]>([]);

// Franchises data (edit mode only)
const selectedFranchises = ref<string[]>([]);
const availableFranchises = ref<AvailableFranchise[]>([]);
const loadingFranchises = ref<boolean>(false);
const initialFranchises = ref<string[]>([]);

// Profile picture (edit mode only)
const profileImage = ref<File | null>(null);

// User data (edit mode only)
const userData = ref<User | null>(null);

// Language options (loaded from translations-service on mount, with static fallback)
const { fetchLanguages } = useLanguages();
const languageOptions = ref<LanguageOption[]>([
  { ID: 'pt-BR', Name: 'Português (BR)' },
  { ID: 'en-US', Name: 'English (US)' },
  { ID: 'es-ES', Name: 'Español (ES)' }
]);

// Computed
// Pula a etapa de franquias (skill app-groups): conta Full (vê todas), quem está cadastrando não
// vincula franquia (administração delegada — capacidade do app-api) ou a conta só tem grupos de
// app (no app ela vê o que o app decide; na plataforma, nenhuma unidade).
const { carregar: carregarRegras, deveLimpar, soGruposDeApp } = useRegrasDeGrupos();
void carregarRegras();
/** Os editores de acesso (skill app-groups) devolvem o mapa atributo → valor; o formulário guarda os dois campos do contrato. */
const aplicaValoresDeAtributo = (valores: Record<string, string>): void => {
  formData.value.mcp_scopes = valores.mcp_scopes ?? '';
  formData.value.dw_scopes = valores.dw_scopes ?? '';
};

const pulaFranquias = computed<boolean>(
  () => selectedGroups.value.includes('Full')
    || !permissionsService.hasCapability('vincula_franquias')
    || soGruposDeApp(selectedGroups.value)
);

// Wizard steps configuration
const wizardSteps = computed<WizardStep[]>(() => {
  if (isEditMode.value) {
    // Edit mode: 4 steps (Info, Permissions, Franchises, Photo)
    return [
      {
        title: t('Informações'),
        description: t('Dados básicos do usuário'),
        validate: () => isStep0Valid.value
      },
      {
        title: t('Permissões'),
        description: t('Grupos de acesso'),
        validate: () => selectedGroups.value.length > 0
      },
      {
        title: t('Franquias'),
        description: t('Acesso por franquia'),
        validate: () => pulaFranquias.value || selectedFranchises.value.length > 0
      },
      {
        title: t('Foto de Perfil'),
        description: t('Imagem do usuário (opcional)'),
        validate: () => true
      }
    ];
  } else {
    // Create mode: 2 steps (Info + Review)
    return [
      {
        title: t('Informações'),
        description: t('Dados do usuário'),
        icon: 'pi pi-user',
        validate: () => isStep0Valid.value
      },
      {
        title: t('Revisão'),
        description: t('Confirme os dados'),
        icon: 'pi pi-check',
        validate: () => true
      }
    ];
  }
});

// Edit mode button configuration
const nextButtonLabel = computed(() => {
  return (step: number): string => {
    if (isLoading.value) {
      return t('Processando...');
    }

    // Last step (step 3 - Profile Picture)
    if (step === 3) {
      return profileImage.value ? t('Finalizar com Foto') : t('Finalizar');
    }

    // All other steps show "Próximo"
    return t('Próximo');
  };
});

const disableNextButton = computed(() => {
  return (step: number): boolean => {
    if (isLoading.value) return true;

    const stepConfig = wizardSteps.value[step];
    return Boolean(stepConfig && !stepConfig.validate());
  };
});

// Load user data on mount (edit mode only)
onMounted(async () => {
  log('UserWizard', 'onMounted', 'Component mounted');
  // Standard modal events (for centralized ModalManager)
  eventBus.on('modal.user-wizard.open', handleModalOpen);
  eventBus.on('modal.user-wizard.close', handleClose);

  try {
    const langs = await fetchLanguages() as unknown as LanguageOption[];
    if (langs.length > 0) {
      languageOptions.value = langs;
    }
  } catch (err) {
    log('UserWizard', 'onMounted', 'Failed to load languages, using defaults', err);
  }

  // Initial load if opened via props (backward compatibility)
  if (isEditMode.value && props.user) {
    await loadUserData();
    await loadGroups();
    await loadUserPermissions();
  }
});

onBeforeUnmount(() => {
  log('UserWizard', 'onBeforeUnmount', 'Component unmounting');
  eventBus.off('modal.user-wizard.open', handleModalOpen);
  eventBus.off('modal.user-wizard.close', handleClose);
});

// Load user data (edit mode)
const loadUserData = async (): Promise<void> => {
  const user = currentUser.value;
  // Capture identity of the user being loaded so a slow fetch can't overwrite
  // formData if the user switched mid-flight. Without this guard, opening
  // A → B → A in quick succession lets A's fetch land after B's, leaving B's
  // modal populated with A's data.
  const requestedSub = user?.sub ?? null;
  try {
    isLoading.value = true;
    log('UserWizard', 'loadUserData', 'Loading user data', user);

    let fullUserData: User;
    if (user?.sub && !user?.username) {
      fullUserData = await fetchUserBySub(user.sub) as User;
    } else {
      fullUserData = (user?.Usuario || user || {}) as User;
    }

    if (currentUser.value?.sub !== requestedSub) {
      log('UserWizard', 'loadUserData', 'Stale fetch dropped (user changed mid-load)', { requestedSub, currentSub: currentUser.value?.sub });
      return;
    }

    userData.value = fullUserData;

    formData.value = {
      username: fullUserData.username || '',
      name: fullUserData.name || '',
      email: fullUserData.email || '',
      password: '',
      confirmPassword: '',
      phone_number: fullUserData.phone_number || '',
      documento: fullUserData.documento || '',
      language: fullUserData.language || 'pt-BR',
      versaoSistema: String(fullUserData.versaoSistema ?? '0'),
      bgcolor: fullUserData.bgcolor || '#3c8dbc',
      mcp_scopes: fullUserData.mcp_scopes || '',
      dw_scopes: fullUserData.dw_scopes || ''
    };

    log('UserWizard', 'loadUserData', 'User data loaded', formData.value);
  } catch (error: unknown) {
    log('UserWizard', 'loadUserData', 'Error loading user data', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados do usuário'));
  } finally {
    if (currentUser.value?.sub === requestedSub) {
      isLoading.value = false;
    }
  }
};

// Load available groups (edit mode)
const loadGroups = async (): Promise<void> => {
  try {
    loadingGroups.value = true;
    availableGroups.value = await fetchUserGroups();
    log('UserWizard', 'loadGroups', 'Groups loaded', availableGroups.value);
  } catch (error: unknown) {
    log('UserWizard', 'loadGroups', 'Error loading groups', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar grupos'));
  } finally {
    loadingGroups.value = false;
  }
};

// Load user permissions (edit mode)
const loadUserPermissions = async (): Promise<void> => {
  try {
    const userSub = userData.value?.sub || props.user?.sub;
    if (!userSub) return;

    const permissions = await fetchUserPermissions(userSub) as UserPermissions;

    selectedGroups.value = permissions.Groups || permissions.groups || [];
    initialGroups.value = [...selectedGroups.value];

    const franquiasObj = permissions.Franquias || permissions.franquias || {};
    selectedFranchises.value = Object.keys(franquiasObj);
    initialFranchises.value = [...selectedFranchises.value];

    log('UserWizard', 'loadUserPermissions', 'Permissions loaded', {
      groups: selectedGroups.value,
      franchises: selectedFranchises.value
    });
  } catch (error: unknown) {
    log('UserWizard', 'loadUserPermissions', 'Error loading permissions', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar permissões'));
  }
};

// Load franchises (edit mode)
const loadFranchises = async (): Promise<void> => {
  loadingFranchises.value = true;
  try {
    const franchises = await permissionsService.getFranquias() as Record<string, FranchiseEntry>;

    availableFranchises.value = Object.keys(franchises || {}).map(id => ({
      id: id,
      name: franchises[id]?.Dados?.Nome || franchises[id]?.Nome || 'Sem nome',
      location: franchises[id]?.Dados?.Cidade || franchises[id]?.Cidade || '',
      status: 'active'
    }));

    log('UserWizard', 'loadFranchises', 'Franchises loaded', availableFranchises.value);
  } catch (error: unknown) {
    log('UserWizard', 'loadFranchises', 'Error loading franchises', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar franquias'));
  } finally {
    loadingFranchises.value = false;
  }
};

// Watch for step changes (edit mode only)
watch(currentStep, async (newStep, oldStep) => {
  if (!isEditMode.value) return;

  log('UserWizard', 'step-change', 'Step changed', { from: oldStep, to: newStep });

  // In edit mode with free navigation, we load data when entering certain steps
  // but don't auto-save on leaving steps

  // Load franchises when entering step 2 (if not already loaded)
  if (newStep === 2 && availableFranchises.value.length === 0) {
    await loadFranchises();
  }
}, { immediate: false });

// Step 0: Validation handler
const handleStep0Validate = (isValid: boolean, errors: FormErrors): void => {
  isStep0Valid.value = isValid;
  formErrors.value = errors;
};

// Note: Step completion handlers removed - all changes are now saved
// in handleEditFinish() to support free navigation between steps

// Finish wizard
const handleFinish = async (): Promise<void> => {
  // Guard against double-submit: in create mode the default WizardModal save
  // button isn't gated by `disable-next`, so a user can fire a second click
  // before the modal closes and trigger a duplicate AdminCreateUser
  // (UsernameExistsException). isLoading is set inside handleCreate/EditFinish.
  if (isLoading.value) {
    log('UserWizard', 'handleFinish', 'Submission already in flight, ignoring');
    return;
  }
  if (isEditMode.value) {
    await handleEditFinish();
  } else {
    await handleCreateFinish();
  }
};

// Create mode finish
const handleCreateFinish = async (): Promise<void> => {
  try {
    isLoading.value = true;
    log('UserWizard', 'handleCreateFinish', 'Creating user', formData.value);

    const createPayload = {
      username: formData.value.username,
      name: formData.value.name,
      email: formData.value.email,
      documento: formData.value.documento,
      phone_number: formData.value.phone_number,
      password: formData.value.password,
      language: formData.value.language,
      bgcolor: formData.value.bgcolor,
      mcp_scopes: formData.value.mcp_scopes
    };

    await createUser(createPayload);

    log('UserWizard', 'handleCreateFinish', 'User created successfully');

    eventBus.emit('globalMessage.success', t('Usuário criado com sucesso'));

    eventBus.emit('users.refresh');
    eventBus.emit('user-wizard.saved');
    handleClose();

  } catch (error: unknown) {
    log('UserWizard', 'handleCreateFinish', 'Error creating user', error);
    const message = error instanceof Error ? error.message : t('Erro ao criar usuário');
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
  }
};

// Edit mode finish
const handleEditFinish = async (): Promise<void> => {
  try {
    isLoading.value = true;
    log('UserWizard', 'handleEditFinish', 'Finishing wizard - saving all changes');

    if (!userData.value?.sub) {
      throw new Error(t('Erro ao atualizar informações do usuário'));
    }

    const userSub = userData.value.sub;
    const userUsername = userData.value.username as string;

    // Leaving the group that unlocks an attribute revokes the attribute too — each one by ITS
    // group (skill app-groups: MCP → mcp_scopes; the tenant's data-warehouse group → dw_scopes).
    if (deveLimpar('mcp_scopes', selectedGroups.value)) {
      formData.value.mcp_scopes = '';
    }
    if (deveLimpar('dw_scopes', selectedGroups.value)) {
      formData.value.dw_scopes = '';
    }

    // Step 1: Update basic user information
    try {
      log('UserWizard', 'handleEditFinish', 'Updating user info');
      const updatePayload = {
        username: formData.value.username,
        name: formData.value.name,
        email: formData.value.email,
        phone_number: formData.value.phone_number,
        documento: formData.value.documento,
        language: formData.value.language,
        versaoSistema: formData.value.versaoSistema,
        bgcolor: formData.value.bgcolor,
        // Send only when set now or before (so it can be cleared): a pool without
        // the custom:mcp_scopes attribute rejects the whole update if the key is present.
        ...(formData.value.mcp_scopes || userData.value.mcp_scopes
          ? { mcp_scopes: formData.value.mcp_scopes }
          : {}),
        // Same rule for the sibling attribute.
        ...(formData.value.dw_scopes || userData.value.dw_scopes
          ? { dw_scopes: formData.value.dw_scopes }
          : {})
      };

      await updateUser(updatePayload);
      log('UserWizard', 'handleEditFinish', 'User info updated');
    } catch (error: unknown) {
      log('UserWizard', 'handleEditFinish', 'Error updating user info', error);
      // Preserve the underlying message (e.g. contract validator detail) so the
      // user-facing toast surfaces *why* the update failed instead of a generic
      // "Erro ao atualizar informações do usuário" that hides the root cause.
      throw error instanceof Error
        ? error
        : new Error(t('Erro ao atualizar informações do usuário'));
    }

    // Step 2: Update permissions (groups)
    try {
      log('UserWizard', 'handleEditFinish', 'Updating groups');
      const addedGroups = selectedGroups.value.filter(g => !initialGroups.value.includes(g));
      const removedGroups = initialGroups.value.filter(g => !selectedGroups.value.includes(g));

      if (addedGroups.length > 0 || removedGroups.length > 0) {
        log('UserWizard', 'handleEditFinish', 'Group changes', { added: addedGroups, removed: removedGroups });

        for (const group of [...addedGroups, ...removedGroups]) {
          await toggleUserGroup(userUsername, group);
        }

        log('UserWizard', 'handleEditFinish', 'Groups updated');
      }
    } catch (error: unknown) {
      log('UserWizard', 'handleEditFinish', 'Error updating groups', error);
      throw new Error(t('Erro ao atualizar permissões'));
    }

    // Step 3: Update franchises (if not Full permission)
    if (!pulaFranquias.value) {
      try {
        log('UserWizard', 'handleEditFinish', 'Updating franchises');
        const addedFranchises = selectedFranchises.value.filter(f => !initialFranchises.value.includes(f));
        const removedFranchises = initialFranchises.value.filter(f => !selectedFranchises.value.includes(f));

        if (addedFranchises.length > 0 || removedFranchises.length > 0) {
          log('UserWizard', 'handleEditFinish', 'Franchise changes', { added: addedFranchises, removed: removedFranchises });

          for (const franchiseId of [...addedFranchises, ...removedFranchises]) {
            await toggleUserFranquia(userSub, Number(franchiseId));
          }

          log('UserWizard', 'handleEditFinish', 'Franchises updated');
        }
      } catch (error: unknown) {
        log('UserWizard', 'handleEditFinish', 'Error updating franchises', error);
        throw new Error(t('Erro ao atualizar franquias'));
      }
    }

    // Step 4: Upload profile image (if provided)
    if (profileImage.value) {
      try {
        log('UserWizard', 'handleEditFinish', 'Uploading profile image');
        await uploadProfileImage(userSub, profileImage.value);
        log('UserWizard', 'handleEditFinish', 'Profile image uploaded successfully');
      } catch (error: unknown) {
        log('UserWizard', 'handleEditFinish', 'Error uploading image (non-critical)', error);
        eventBus.emit('globalMessage.error', t('Usuário atualizado mas falha no upload da imagem'));
      }
    }

    // Success!
    eventBus.emit('globalMessage.success', t('Usuário atualizado com sucesso'));

    eventBus.emit('users.refresh');
    eventBus.emit('user-wizard.saved');
    handleClose();

  } catch (error: unknown) {
    log('UserWizard', 'handleEditFinish', 'Error finishing wizard', error);
    const message = error instanceof Error ? error.message : t('Erro ao finalizar atualização');
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
  }
};

// Get language name for review
const getLanguageName = (langCode: string): string => {
  const option = languageOptions.value.find(opt => opt.ID === langCode);
  return option ? option.Name : langCode;
};

// Cancel handler
const handleCancel = (): void => {
  log('UserWizard', 'handleCancel', 'Wizard cancelled');
  handleClose();
};

// Close handler
const handleClose = (): void => {
  log('UserWizard', 'handleClose', 'Wizard closed');
  // Reset internal state
  internalShow.value = false;
  internalMode.value = 'create';
  internalUser.value = null;
  // Reset formData so the next open doesn't mount InputPhone with stale data.
  // Without this, InputPhone.onMounted sees the previous user's phone, calls
  // handleInput() for mask formatting, which sets isTyping=true for 500ms —
  // and the fetched correct data arrives within that window and gets dropped
  // by InputPhone's "Skipping - user is typing" guard.
  userData.value = null;
  formData.value = {
    username: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone_number: '',
    documento: '',
    language: 'pt-BR',
    versaoSistema: '0',
    bgcolor: '#3c8dbc',
    mcp_scopes: '',
  dw_scopes: ''
  };
  selectedGroups.value = [];
  initialGroups.value = [];
  selectedFranchises.value = [];
  initialFranchises.value = [];
  profileImage.value = null;
  // Emit via eventBus for centralized pattern
  eventBus.emit('user-wizard.closed');
};
</script>

<style scoped>
.wizard-step-content {
  padding: 0;
}

.alert {
  padding: 10px 15px;
  margin-bottom: 15px;
  border: 1px solid transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-success {
  color: #3c763d;
  background-color: #dff0d8;
  border-color: #d6e9c6;
}

/* Review Step Styles */
.review-step {
  padding: 10px 0;
}

.review-section {
  margin-bottom: 25px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid var(--app-primary-color, #3c8dbc);
}

.review-section h4 {
  margin: 0 0 15px 0;
  color: var(--app-primary-color, #3c8dbc);
  font-size: 1.1em;
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.review-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.review-item:last-child {
  border-bottom: none;
}

.review-label {
  font-weight: 600;
  color: #555;
  min-width: 120px;
  margin-right: 15px;
}

.review-value {
  color: #333;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-preview {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid #ddd;
  vertical-align: middle;
}

/* Responsive */
@media (max-width: 768px) {
  .review-section {
    padding: 10px;
  }

  .review-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .review-label {
    min-width: auto;
    margin-bottom: 4px;
  }
}
</style>
